import React, { useState, useEffect } from 'react';
import { CheckCircle2, X, Clock, Thermometer, AlertTriangle, TrendingUp, Award, Shield, Star } from 'lucide-react';
import SEO from '../../components/SEO';
import CheckoutButton from '../../components/CheckoutButton';

const HVACOffer = () => {
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 47, seconds: 0 });
  const [spotsLeft] = useState(5);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { hours: prev.hours, minutes: prev.minutes - 1, seconds: 59 };
        if (prev.hours > 0) return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <SEO 
        title="HVAC Companies: Free £8,400/Year Upgrade - Limited to 5 Companies"
        description="Stop losing £3,200/month to missed calls during peak season. Get Hero Domination features at Hero Starter price. Forever."
      />
      
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#1a1f3a] to-[#0A0E27]">
        <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white py-3 text-center font-bold sticky top-0 z-50 shadow-lg">
          <div className="container mx-auto px-4 flex items-center justify-center space-x-4">
            <Thermometer className="animate-pulse" size={20} />
            <span>PEAK SEASON OFFER ENDS IN: {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s</span>
            <span className="bg-white/20 px-3 py-1 rounded-full text-sm">Only {spotsLeft} HVAC companies left</span>
          </div>
        </div>

        <div className="container mx-auto px-4 py-12 max-w-5xl">
          <div className="text-center mb-12">
            <div className="inline-block bg-red-600 text-white px-4 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
              🔥 URGENT: HVAC CONTRACTORS ONLY
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              It's 92° Outside and Your Phone<br />
              Is Ringing Off The Hook...<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-500">But You're Missing 67% of Them</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              <strong className="text-white">FREE £8,400/Year Upgrade For 5 HVAC Companies</strong><br />
              Get Every Call Answered. Every Quote Sent. Every Follow-Up Automated.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur rounded-2xl p-8 mb-12 border border-white/10">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">
              Peak Season Reality Check...
            </h2>
            
            <div className="space-y-4">
              {[
                "It's 6 PM. Someone's AC just died. They called you first. You didn't answer. They booked your competitor by 6:04 PM. Lost: £2,400.",
                "You've got 73 voicemails from this week. 41 are still 'urgent'. You'll never call them all back. Lost: £14,760.",
                "Mrs. Henderson wanted a new system installed. You quoted £8,500. You forgot to follow up. She went with the guy who DID follow up.",
                "Your calendar is double-booked AGAIN. Customer waited 4 hours. Left a 1-star review. Cost you 12 jobs this month.",
                "It's January. Work dried up. You're scrambling. If only you'd captured more leads during summer...",
                "You're bidding on a £45,000 commercial job. They asked for references. You have 7 reviews. Competitor has 127. Guess who won?"
              ].map((problem, i) => (
                <div key={i} className="flex items-start space-x-3 bg-red-900/20 p-4 rounded-lg border border-red-500/30">
                  <AlertTriangle className="text-red-500 flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-200">{problem}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-2xl text-orange-400 font-bold mb-2">
                Peak season = Peak chaos
              </p>
              <p className="text-gray-300 text-lg">
                You're leaving <strong className="text-white">£38,400 on the table every summer</strong> because your systems can't keep up.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-500/20 to-red-500/20 border-2 border-orange-500 rounded-2xl p-8 mb-12">
            <div className="text-center mb-8">
              <Award className="mx-auto text-orange-400 mb-4" size={48} />
              <h2 className="text-4xl font-black text-white mb-4">
                What If Peak Season Ran Itself?
              </h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "AI Answers Every Call - Even at 10 PM",
                  desc: "Customer's AC dies at night? AI takes the call, gets all details, books emergency slot, sends confirmation. All while you sleep."
                },
                {
                  title: "Quotes Sent in 60 Seconds",
                  desc: "Customer asks 'how much?' System sends professional quote instantly. With photos. With financing options. They book on the spot."
                },
                {
                  title: "Maintenance Reminders = Recurring Revenue",
                  desc: "System texts every customer 12 months after install: 'Time for your annual service!' 71% book immediately."
                },
                {
                  title: "Follow-Ups That Actually Convert",
                  desc: "Every quote gets 7 automatic follow-ups. Text, email, voicemail. Conversion rate jumps from 23% to 64%."
                },
                {
                  title: "Reviews Pour In Automatically",
                  desc: "Finish a job. System texts customer 2 hours later. 'How'd we do?' 58% leave 5-star reviews within 24 hours."
                },
                {
                  title: "Fill Your Winter Calendar in Summer",
                  desc: "Capture EVERY lead during peak season. Nurture them automatically. When winter hits, your calendar is already full."
                }
              ].map((benefit, i) => (
                <div key={i} className="bg-white/10 backdrop-blur rounded-xl p-6 border border-orange-500/30">
                  <CheckCircle2 className="text-green-400 mb-3" size={32} />
                  <h3 className="text-xl font-bold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-300">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-12">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              Real HVAC Companies, Real Numbers
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  name: "Tom Rodriguez",
                  business: "Cool Air HVAC, Phoenix",
                  result: "+£67,000 in one summer",
                  quote: "Last summer I missed probably 200 calls. This year? Zero. The AI answered everything. I hired 3 more techs and still can't keep up with demand."
                },
                {
                  name: "Jessica Park",
                  business: "Park Climate Control",
                  result: "4.9 stars with 183 reviews",
                  quote: "The review automation is insane. We went from 14 reviews to 183 in 4 months. Now we're #1 in our area. Leads are up 340%."
                },
                {
                  name: "Robert Williams",
                  business: "Williams HVAC & Heating",
                  result: "£127K winter revenue (used to be £34K)",
                  quote: "I captured every lead during summer. System nurtured them automatically. When December hit, I had 47 maintenance contracts already booked. This changed everything."
                }
              ].map((testimonial, i) => (
                <div key={i} className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10">
                  <div className="flex items-center mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                    ))}
                  </div>
                  <p className="text-gray-200 mb-4 italic">"{testimonial.quote}"</p>
                  <div className="border-t border-white/10 pt-4">
                    <p className="font-bold text-white">{testimonial.name}</p>
                    <p className="text-sm text-gray-400">{testimonial.business}</p>
                    <p className="text-orange-400 font-bold mt-2">{testimonial.result}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 border-4 border-green-500 rounded-2xl p-8 mb-12">
            <div className="text-center mb-8">
              <h2 className="text-4xl font-black text-white mb-4">
                The Offer (Only 5 HVAC Companies Get This)
              </h2>
              <p className="text-xl text-gray-200">
                Expires in {timeLeft.hours}h {timeLeft.minutes}m {timeLeft.seconds}s
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white/5 rounded-xl p-6">
                <h3 className="text-2xl font-bold text-white mb-4">Hero Starter</h3>
                <p className="text-5xl font-black text-white mb-4">£297<span className="text-xl text-gray-400">/mo</span></p>
                <p className="text-gray-400 mb-4">What you'd normally get</p>
                <ul className="space-y-3">
                  {["10 users", "1,000 contacts", "Basic features"].map((feature, i) => (
                    <li key={i} className="flex items-center text-gray-300">
                      <CheckCircle2 className="text-gray-500 mr-2" size={20} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-500/30 to-red-500/30 rounded-xl p-6 border-4 border-orange-500 relative">
                <div className="absolute -top-3 -right-3 bg-green-600 text-white px-4 py-1 rounded-full text-sm font-bold animate-pulse">
                  YOUR PRICE FOREVER
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Hero Domination</h3>
                <p className="text-5xl font-black text-white mb-4">£297<span className="text-xl text-gray-400">/mo</span></p>
                <p className="text-orange-400 font-bold mb-4">Save £700/mo = £8,400/year</p>
                <ul className="space-y-3">
                  {[
                    "50 users (5x more)",
                    "UNLIMITED contacts",
                    "Done-for-you setup",
                    "Dedicated success manager",
                    "Priority support"
                  ].map((feature, i) => (
                    <li key={i} className="flex items-center text-white font-medium">
                      <CheckCircle2 className="text-green-400 mr-2" size={20} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="text-center">
              <CheckoutButton 
                plan="STARTER"
                className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white text-2xl px-12 py-6 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all mb-4"
              >
                CLAIM MY LIFETIME UPGRADE - £5 Trial
              </CheckoutButton>
              <p className="text-gray-300 text-sm">14-day trial, then £297/mo with Domination features forever</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-600 to-orange-600 rounded-2xl p-8 text-center">
            <h2 className="text-4xl font-black text-white mb-4">
              {spotsLeft} Spots Left. Then It's Gone Forever.
            </h2>
            <CheckoutButton 
              plan="STARTER"
              className="bg-white text-red-600 text-2xl px-12 py-6 rounded-full font-black shadow-2xl transform hover:scale-105 transition-all hover:bg-gray-100"
            >
              YES! UPGRADE ME NOW - Start £5 Trial
            </CheckoutButton>
          </div>

        </div>
      </div>
    </>
  );
};

export default HVACOffer;
